import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from './client';

// ===== User Types =====

// FBV-01: Claude tier values. Used by CreateUserInput and restoreAdminUser.
export type ClaudeTier = 'claude-standard' | 'claude-premium';
// 'none' = person needs no Claude licence (backend skips the Claude onboarding steps).
export type ClaudeTierChoice = ClaudeTier | 'none';

export interface AdminUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
  employeeTypeId: number | null;
  startDate: string | null;
  endDate: string | null;
  deletedAt: string | null;
  createdAt: string | null;
  currentHourlyCosts: number | null;
  githubUsername: string | null;
  hasLLMCondom: boolean;
  slackUserId: string | null;
  phoneNumber: string | null;
  // Quick 260701-eor — birth date (PII). Nullable; returned from detail/update
  // responses once declared on the backend userSchema (T-eor-FJS lock).
  birthDate: string | null;
  // Phase 71 (VAC-01, D-05) — vacation entitlement (days, default 20) and
  // the one-time manual carry-in seed (hours). Both are entitlement CONFIG
  // values, not balances — the per-user BALANCE comes from useVacationBalance.
  vacationEntitlementDays: number | null;
  vacationCarryInSeed: number | null;
  // Phase 84 follow-up — onboarding buddy: who looks after this person when
  // they join. Read by the buddy-bonus rule and the onboarding flow.
  buddyId?: number | null;
  buddy?: { id: number; firstName: string | null; lastName: string | null; email: string } | null;
}

export interface CreateUserInput {
  email: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  employeeTypeId?: number;
  startDate?: string;
  personalEmail?: string | null;
  skipOnboarding?: boolean;
  githubUsername?: string | null;
  /** Phase 84 follow-up — onboarding buddy, optional at creation. */
  buddyId?: number | null;
  // FBV-01: optional Claude tier. Omitting defaults to 'claude-standard' on the backend.
  claudeTier?: ClaudeTierChoice;
}

export interface UpdateUserInput {
  firstName?: string;
  lastName?: string;
  roles?: string[];
  employeeTypeId?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  hourlyCosts?: number | null;
  githubUsername?: string | null;
  slackUserId?: string | null;
  phoneNumber?: string | null;
  // Quick 260701-eor — birth date (PII). Normalized to YYYY-MM-DD on submit.
  birthDate?: string | null;
  // Phase 71 (VAC-01, D-05) — HR/admin-editable entitlement (days) + the
  // one-time manual prior-year carryover seed (hours). null clears either
  // field back to "no override" / "no manual seed entered".
  vacationEntitlementDays?: number | null;
  vacationCarryInSeed?: number | null;
  /** Phase 84 follow-up — onboarding buddy. null clears it. */
  buddyId?: number | null;
}

export interface DeleteUserOptions {
  skipOffboarding?: boolean;
  driveTransferTo?: string;
}

// ===== User Rate Types =====
export interface UserRate {
  id: number;
  userId: number;
  validFrom: string;
  validTo: string | null;
  hourlyCosts: number | null;
  salary: number | null;
  monthlyCosts: number | null;
  partTime: number;
  employeeTypeId: number | null;
}

export interface CreateUserRateInput {
  validFrom: string;
  validTo?: string | null;
  hourlyCosts: number;
  salary?: number | null;
  partTime?: number;
  employeeTypeId?: number;
  excludeAssignmentIds?: number[];
}

export interface UpdateUserRateInput {
  validFrom?: string;
  validTo?: string | null;
  hourlyCosts?: number;
  salary?: number | null;
  partTime?: number;
}

// ===== User Assignment Types =====
export interface UserAssignment {
  id: number;
  userId: number;
  projectId: number;
  startDate: string | null;
  endDate: string | null;
  billingRate: number | null;
  hourlyCosts: number | null;
  userInvestment: number | null;
  roleName: string | null;
  deleted_at: string | null;
  project: {
    id: number;
    name: string | null;
    fullName: string | null;
    defaultRate: number | null;
    state: string | null;
  } | null;
}

// ===== Period Types =====
export interface Period {
  id: number;
  name: string | null;
  from: string | null;
  to: string | null;
  createdAt: string | null;
}

export interface CreatePeriodInput {
  name: string;
  from: string;
  to: string;
}

// ===== Employee Type Types =====
export interface EmployeeType {
  id: number;
  name: string | null;
  totalHoursPerMonth: number | null;
  createdAt: string | null;
  // Phase 71 (VAC-04, D-02) — 'employee' | 'contractor' | null. Used by
  // UserDetailPage to determine which of a user's 1-2 employment
  // relationships is the employee-category one (FTE-02 hourlyCosts repoint,
  // vacation entitlement gate).
  category: string | null;
}

// ===== User API =====
export async function getAdminUsers(includeDeleted = false): Promise<AdminUser[]> {
  const query = includeDeleted ? '?includeDeleted=true' : '';
  const res = await apiGet<{ data: AdminUser[] }>(`/admin/users${query}`);
  return res.data;
}

export async function getAdminUser(id: number): Promise<AdminUser> {
  const res = await apiGet<{ data: AdminUser }>(`/admin/users/${id}`);
  return res.data;
}

export async function createAdminUser(input: CreateUserInput): Promise<AdminUser> {
  const res = await apiPost<{ data: AdminUser }>('/admin/users', input);
  return res.data;
}

export async function updateAdminUser(id: number, input: UpdateUserInput): Promise<AdminUser> {
  const res = await apiPut<{ data: AdminUser }>(`/admin/users/${id}`, input);
  return res.data;
}

export async function deleteAdminUser(id: number, options?: DeleteUserOptions): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/admin/users/${id}`, options);
}

// FBV-01: claudeTier is forwarded in the POST body so the backend can persist
// it on the 'claude' cell and forward it as appAccessTier to the onboarding workflow.
export async function restoreAdminUser(
  id: number,
  claudeTier?: ClaudeTierChoice,
  skipOnboarding?: boolean,
): Promise<void> {
  await apiPost<{ data: { success: boolean } }>(`/admin/users/${id}/restore`, { claudeTier, skipOnboarding });
}

// ===== User Rate API =====
export async function getUserRates(userId: number): Promise<UserRate[]> {
  const res = await apiGet<{ data: UserRate[] }>(`/admin/user-rates/${userId}`);
  return res.data;
}

export async function createUserRate(userId: number, input: CreateUserRateInput): Promise<UserRate> {
  const res = await apiPost<{ data: UserRate }>(`/admin/user-rates/${userId}`, input);
  return res.data;
}

export async function updateUserRate(userId: number, rateId: number, input: UpdateUserRateInput): Promise<UserRate> {
  const res = await apiPut<{ data: UserRate }>(`/admin/user-rates/${userId}/${rateId}`, input);
  return res.data;
}

export async function deleteUserRate(userId: number, rateId: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/admin/user-rates/${userId}/${rateId}`);
}

// ===== User Assignment API =====
export async function getUserAssignments(userId: number, includeDeleted = false, includeInactive = false): Promise<UserAssignment[]> {
  const params = new URLSearchParams();
  if (includeDeleted) params.set('includeDeleted', 'true');
  if (includeInactive) params.set('includeInactive', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await apiGet<{ data: UserAssignment[] }>(`/admin/users/${userId}/assignments${query}`);
  return res.data;
}

// Phase 104 — the User Team Memberships API (Phase 52) was removed here;
// the backend route it called was deleted in Plan 104-03 (D-01 full retire
// of the coverage-grid team model). ProjectAssignment (getUserAssignments,
// above) remains the canonical staffing model (SC-3).

// ===== Period API =====
export async function getPeriods(): Promise<Period[]> {
  const res = await apiGet<{ data: Period[] }>('/admin/periods');
  return res.data;
}

export async function createPeriod(input: CreatePeriodInput): Promise<Period> {
  const res = await apiPost<{ data: Period }>('/admin/periods', input);
  return res.data;
}

export async function updatePeriod(id: number, input: Partial<CreatePeriodInput>): Promise<Period> {
  const res = await apiPut<{ data: Period }>(`/admin/periods/${id}`, input);
  return res.data;
}

export async function deletePeriod(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/admin/periods/${id}`);
}

// ===== Employee Type API =====
export async function getEmployeeTypes(): Promise<EmployeeType[]> {
  const res = await apiGet<{ data: EmployeeType[] }>('/admin/employee-types');
  return res.data;
}

export async function createEmployeeType(input: { name: string; totalHoursPerMonth?: number }): Promise<EmployeeType> {
  const res = await apiPost<{ data: EmployeeType }>('/admin/employee-types', input);
  return res.data;
}

export async function updateEmployeeType(id: number, input: { name?: string; totalHoursPerMonth?: number | null }): Promise<EmployeeType> {
  const res = await apiPut<{ data: EmployeeType }>(`/admin/employee-types/${id}`, input);
  return res.data;
}

export async function deleteEmployeeType(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/admin/employee-types/${id}`);
}

// ===== App Settings Types =====
/**
 * MKS-469 — one team's routing in `linear_team_project_map`: a plain default
 * project id (the original shape, still what a team without label overrides
 * is stored as) or `{ project, labels }`, where `labels` maps a Linear label
 * name onto the project an issue carrying exactly that label belongs to.
 */
export type LinearTeamProjectMapValue =
  | number
  | { project: number; labels?: Record<string, number> };

export interface AppSettings {
  auto_archive_inactive_months: number;
  audit_trail_retention_months: number;
  retention_entries_months: number;
  retention_time_offs_months: number;
  retention_expenses_months: number;
  retention_assignments_months: number;
  retention_invoices_months: number;
  retention_tasks_months: number;
  lead_staleness_days: number;
  engineering_metrics_initial_weeks: number;
  ga4_property_id: string | null;
  // Phase 98 (D-01) — holiday calendar country for the 4 in-scope Time/core holiday call sites.
  holiday_country_code: string;
  // Phase 98 (D-03) — default currency for new projects only; never relabels existing figures.
  // MKS-314 widened the backend enum from the CZK/EUR/USD triple to every code
  // a rate source publishes, so this is a bare string, not a union.
  default_currency: string;
  // MKS-314 — the instance formatting locale (`cs-CZ`, `en-GB`, …). Optional
  // because an older backend does not send the key.
  instance_locale?: string;
  // MKS-314 — the company-wide reporting currency. `null` = not set, which
  // falls back to `default_currency`; that is a real state, not "CZK".
  reporting_currency?: string | null;
  // MKS-314 — which reference rate converts into the reporting currency.
  fx_rate_source?: 'ecb' | 'cnb';
  // Quick task 260908-ku9 — YEARLY invoiced-revenue target (CZK). The Monthly
  // Revenue report divides it by 12 for its per-month target. 0 = no target.
  annual_revenue_target_czk: number;
  // MKS-214 — company-wide invoice payment term in days. A newly issued
  // invoice's due date defaults to its issue date plus this many days; finance
  // can change it on the individual invoice. Optional because an older backend
  // (or a client built before this ticket) does not send the key.
  invoice_default_due_days?: number;
  // Phase 121 (PCATTR-06, D-15) — the global per-person Factory™ attribution kill
  // switch (default off). Written from the Factory™ tab on /admin/bot.
  paperclip_attribution_enabled?: boolean;
  // Phase 122 (PCTOK-09, D-16) — the Claude-token sync off-switch (default off). Written
  // from the Factory™ tab on /admin/bot; only meaningful on the production writer.
  paperclip_token_sync_enabled?: boolean;
  // Phase 124 (PCMEMB-09, D-11) — the Factory™ account provisioning off-switch (default off).
  // Written from the Factory™ tab on /admin/bot; only meaningful on the production writer.
  paperclip_member_provisioning_enabled?: boolean;
  // Phase 125 (PCAGT-13, D-13) — the per-person builder agent switch (default off). Written from
  // the Factory™ tab on /admin/bot. The pilot list (`paperclip_person_agents_pilot_user_ids`)
  // is edited through the settings PATCH during the rollout, not from the UI.
  paperclip_person_agents_enabled?: boolean;
  // Linear team key -> routing (MKS-469: object values exist since this ticket;
  // numeric values are unchanged). Edited on /admin/linear-team-routing.
  linear_team_project_map?: Record<string, LinearTeamProjectMapValue>;
}

// ===== App Settings API =====
export async function getAppSettings(): Promise<AppSettings> {
  const res = await apiGet<{ data: AppSettings }>('/admin/settings');
  return res.data;
}

export async function updateAppSettings(input: Partial<AppSettings>): Promise<AppSettings> {
  const res = await apiPatch<{ data: AppSettings }>('/admin/settings', input);
  return res.data;
}
